import { useCallback, useLayoutEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { toast } from 'sonner';
import { useOrderWorkspaceSession, boundedOrderRead } from './useOrderWorkspaceSession';
import useDesktopDeviceStatus from './useDesktopDeviceStatus';
import { createDispatchDecisions } from './dispatchDecisions';

export default function useDispatchActions({ orders, selectedDateString, driverMode, fetchOrders, acceptDispatchOrder }) {
  const session = useOrderWorkspaceSession();
  const device = useDesktopDeviceStatus();
  const canPlace = session.valid && session.isAdmin && session.offline && !!device?.registered && !!device?.isPrimary && !driverMode;
  const canFlag = session.valid && session.isAdmin && !session.offline && !driverMode;
  const scope = useMemo(() => ({ key: session.key, selectedDateString, driverMode, canPlace, canFlag }), [session.key, selectedDateString, driverMode, canPlace, canFlag]);
  const latest = useRef(); latest.current = { session, scope, orders, canPlace, canFlag, fetchOrders, acceptDispatchOrder };
  const live = useRef(null);
  const controllers = useRef(new Set());
  const read = useCallback((execute, config) => boundedOrderRead(execute, config, controllers.current), []);
  const capture = useCallback(local => {
    const now = latest.current; const owner = now.session.capture(local ?? now.session.offline);
    if (!owner) return null;
    return { ...owner, admin: owner.admin && !now.scope.driverMode, canPlace: now.canPlace,
      current: () => live.current === now.scope && owner.current() && now.scope === latest.current.scope };
  }, []);
  const decisions = useMemo(() => createDispatchDecisions(capture, read), [capture, read]);
  const pending = useSyncExternalStore(decisions.subscribe, decisions.getSnapshot);
  useLayoutEffect(() => {
    live.current = scope; decisions.activate(); const active = controllers.current;
    return () => { live.current = null; decisions.retire(); active.forEach(controller => controller.abort()); };
  }, [decisions, scope]);
  const refresh = useCallback(async (deliveryDate = selectedDateString) => {
    const now = latest.current; const owner = capture(now.session.offline);
    if (!owner?.current()) return false;
    const ids = decisions.getSnapshot().filter(item => item.state === 'accepted' && (now.session.offline || item.date === deliveryDate)).map(item => item.id);
    const result = await now.fetchOrders({ read, config: owner.config, current: owner.current, deliveryDate });
    if (!owner.current() || result?.state !== 'refreshed') return false;
    decisions.refreshed(ids);
    return true;
  }, [capture, decisions, read, selectedDateString]);
  const run = useCallback(async (id, kind, value, openingCurrent = () => true) => {
    const now = latest.current; const order = now.orders.find(item => item.id === id);
    const current = () => live.current === now.scope && now.scope === latest.current.scope && openingCurrent();
    if (order?.delivery_date !== selectedDateString) return { state: 'blocked' };
    const result = await decisions.run(order, kind, value, { current,
      onAccepted: item => now.acceptDispatchOrder(item, kind) });
    if (!current()) return { state: 'retired' };
    if (result.state === 'accepted') {
      toast.success(result.message);
      await refresh();
    } else if (['refused', 'unknown'].includes(result.state)) toast.error(result.message);
    return current() ? result : { state: 'retired' };
  }, [decisions, refresh, selectedDateString]);
  const acknowledge = async id => {
    const claim = decisions.getSnapshot().find(item => item.id === id);
    if (claim && await refresh(claim.date)) decisions.acknowledge(id);
  };
  return { scope, canPlace, canFlag, run, refresh, acknowledge, pending,
    blocked: decisions.blocked, review: decisions.review };
}
